import { HttpClient } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import { environment } from "src/environments/environment";
import {
    CouponRedeem,
    IsPartPayment,
    IsSettlement,
    PaymentStatus,
    RazorpayCallback,
    RazorpayOption,
    RePayCreateOrder,
    RewardWalletClaim,
} from "./re-payment.interface";
import { firstValueFrom, Subscription, take } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { MatDialogSizeService } from "src/app/shared/services/mat-dialog-size";
import { EmiPaymentComponent } from "./component/emi-summary/dialog/emi-payment/emi-payment.component";
import { Router } from "@angular/router";
import { razorpayPaymentOption } from "src/app/shared/constant/razorpay-profile";
import { StoreService } from "src/app/shared/services/store.service";

declare let Razorpay: any;

@Injectable({
    providedIn: "platform",
})
export class RePaymentService {
    subscriptions: Subscription[] = [];
    baseUrl = `${environment.baseUrl}`;
    rePayDetails: any;
    isPartPayment = IsPartPayment;
    isSettlement = IsSettlement;
    paymentDialogOpen = false;
    razorpayInstance: any;

    RAZORPAY_OPTIONS: RazorpayOption = {
        key: "",
        customer_id: 0,
        order_id: "",
        name: razorpayPaymentOption.name,
        theme: razorpayPaymentOption.theme,
        image: razorpayPaymentOption.image,
        prefill: {
            contact: "",
        },
        notes: "",
        handler: () => {},
        modal: {
            ondismiss: () => {},
        },
    };

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private router: Router,
        private matDialogSizeService: MatDialogSizeService,
        private ngZone: NgZone,
        private store: StoreService
    ) {}

    rePayCreateOrder(req: RePayCreateOrder) {
        return this.http.get<any>(`${this.baseUrl}/loan/create/order`, { params: { ...req } });
    }

    getPartPayment(applicationId: string) {
        return this.http.get<any>(`${this.baseUrl}/customer/part/payment/${applicationId}`);
    }

    paymentCallback(req: RazorpayCallback) {
        return this.http.post<any>(`${this.baseUrl}/loan/payment/callback`, req);
    }

    async repaymentSummary(details: any): Promise<boolean> {
        if (!details || Object.keys(details).length === 0) {
            return false;
        }

        const dialogRef = this.dialog.open(EmiPaymentComponent, {
            width: this.matDialogSizeService.wIII,
            disableClose: true,
            data: details,
        });

        const res: any = await firstValueFrom(dialogRef.afterClosed());

        if (!res?.data) {
            return false;
        }

        const isSuccess = await this.createOrder(details);

        if (!isSuccess) {
            return false;
        }

        return true; // ✅ everything successful
    }

    async createOrder(details: any): Promise<boolean> {
        if (!details?.applicationId) {
            return false;
        }

        try {
            const res: any = await firstValueFrom(
                this.rePayCreateOrder({
                    applicationId: details.applicationId,
                    amount: details.amount || 0,
                    payId: details.payId || 0,
                    isForeClose: details.isForeClose || false,
                })
            );

            if (res?.success && Object.keys(res?.data || {}).length > 0) {
                await this.payToRazorpay(res.data, details.applicationId);
                return true;
            }

            return false;
        } catch (err) {
            return false;
        }
    }

    payToRazorpay(orderDetails: any = {}, applicationId: number): Promise<boolean> {
        return new Promise((resolve) => {
            if (!orderDetails || Object.keys(orderDetails).length === 0) {
                resolve(false);
                return;
            }

            let details: any = this.store.get("customerDetails");
            if (typeof details === "string") {
                details = JSON.parse(details);
            }

            const options: RazorpayOption = {
                key: orderDetails.keyId,
                customer_id: orderDetails.razorPayCustomerId,
                order_id: orderDetails.orderId,
                name: razorpayPaymentOption.name,
                theme: razorpayPaymentOption.theme,
                image: razorpayPaymentOption.image,
                notes: orderDetails.paymentNotes,
                prefill: {
                    contact: details?.mobile || "",
                },

                handler: (response: any) => {
                    this.onRazorpayPaymentSuccess(response, applicationId)
                        .then(() => resolve(true)) // ✅ payment success
                        .catch(() => resolve(false));
                },

                modal: {
                    ondismiss: () => {
                        resolve(false);
                    },
                },
            };

            const razorpay = new Razorpay(options);

            razorpay.on("payment.failed", (error: any) => {
                this.onRazorpayPaymentError(error, applicationId);
                resolve(false);
            });

            razorpay.open();
        });
    }

    onRazorpayPaymentSuccess(response: any, applicationId: number): Promise<boolean> {
        const details: RazorpayCallback = {
            applicationId: applicationId || 0,
            paymentId: response?.razorpay_payment_id || "",
            orderId: response?.razorpay_order_id || "",
            signature: response?.razorpay_signature || "",
            status: PaymentStatus.YES,
        };

        return this.router.navigate(["/loan/repay-successful"], { state: details });
    }

    onRazorpayPaymentError(response: any, applicationId: number) {
        this.router.navigate(["/loan/info"], {
            state: {
                isBrandLogoVisible: false,
                icon: "assets/svg/icons/cross-circle-gb.svg",
                title: "Payment didn’t go through. Please try again.",
                rightButtonName: "Retry",
                rightButtonRoute: "/loan/re-pay",
            },
        });

        const updatedRes = response?.error?.metadata || {};
        const details: RazorpayCallback = {
            applicationId: applicationId || 0,
            paymentId: updatedRes?.payment_id || "",
            orderId: updatedRes?.order_id || "",
            signature: "",
            status: PaymentStatus.NO,
        };

        return this.razorpayCallback(details);
    }

    razorpayCallback(details: RazorpayCallback): Promise<boolean> {
        return new Promise((resolve) => {
            if (!details) {
                resolve(false);
                return;
            }

            this.paymentDialogOpen = true;

            this.subscriptions.push(
                this.paymentCallback({ ...details })
                    .pipe(take(1))
                    .subscribe({
                        next: (res: any) => {
                            if (res?.success && Object.keys(res?.data || {}).length > 0) {
                                this.ngZone.run(() => {
                                    this.router.navigate(["/loan/repay-successful"], { state: res.data });
                                });
                                resolve(true); // ✅ backend confirmed
                            } else {
                                resolve(false);
                            }
                        },
                        error: () => {
                            resolve(false);
                        },
                    })
            );
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
    }

    rewardWalletClaim(req: RewardWalletClaim) {
        return this.http.post<any>(`${this.baseUrl}/reward-wallet/claim`, req);
    }

    removeRewardWalletClaim(rewardTransactionRefId?: string) {
        return this.http.get<any>(`${this.baseUrl}/reward-wallet/claim-remove/${rewardTransactionRefId}`);
    }

    couponRedeem(req: CouponRedeem) {
        return this.http.post<any>(`${this.baseUrl}/coupon-redemption/redeem`, req);
    }

    removeCoupon() {
        return this.http.delete<any>(`${this.baseUrl}/coupon-redemption/remove`);
    }
}
